import React, { useState, useCallback } from 'react';
import type {
  UploadFile,
  UploaderConfig,
  WithUploaderProps,
} from '../types/uploader';

const generateId = () => Math.random().toString(36).substr(2, 9);

export const withUploader = <P extends object>(
  WrappedComponent: React.ComponentType<P & WithUploaderProps>
) => {
  return (props: P & { config?: UploaderConfig }) => {
    const { config = {}, ...restProps } = props;

    const [files, setFiles] = useState<UploadFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const validateFile = useCallback(
      (file: File): string | null => {
        const maxFileSize = config.maxFileSize || 10 * 1024 * 1024; // 10MB
        const acceptedTypes = config.acceptedTypes || ['*/*'];

        // Check file size
        if (file.size > maxFileSize) {
          return `The file is too big, it's over ${Math.round(maxFileSize / (1024 * 1024))}MB`;
        }

        // Check file type
        const isAccepted = acceptedTypes.some(type => {
          if (type === '*/*') return true;
          if (type.endsWith('/*')) {
            return file.type.startsWith(type.slice(0, -1));
          }
          return file.type === type;
        });

        if (!isAccepted) {
          return `This file format isn't supported.`;
        }

        return null;
      },
      [config]
    );

    const addFiles = useCallback(
      (newFiles: FileList | File[], callback?: () => void) => {
        setErrors([]);
        const fileArray = Array.from(newFiles);
        const maxFiles = config.maxFiles || 10;
        const validFiles: UploadFile[] = [];
        const validationErrors: string[] = [];

        fileArray.forEach(file => {
          const error = validateFile(file);
          if (error) {
            validationErrors.push(error);
            return;
          }

          validFiles.push({
            id: generateId(),
            file,
            name: file.name,
            size: file.size,
            status: 'pending',
          });
        });

        if (validationErrors.length > 0) {
          setErrors(validationErrors);
          return;
        }

        if (validFiles.length > 0) {
          setFiles(prev => {
            const newFilesList = [...prev, ...validFiles];
            return newFilesList.slice(0, maxFiles);
          });
        }

        if (callback) callback();
      },
      [config, validateFile]
    );

    const removeFile = useCallback((id: string) => {
      setFiles(prev => prev.filter(file => file.id !== id));
    }, []);

    const clearFiles = useCallback(() => {
      setFiles([]);
      setErrors([]);
    }, []);

    const clearErrors = useCallback(() => {
      setErrors([]);
    }, []);

    const uploadFiles = useCallback(async () => {
      if (!config.onUpload) return;

      const pendingFiles = files.filter(file => file.status === 'pending');
      if (pendingFiles.length === 0) return;

      setIsUploading(true);

      for (const file of pendingFiles) {
        setFiles(prev =>
          prev.map(f =>
            f.id === file.id ? { ...f, status: 'uploading' as const } : f
          )
        );

        try {
          const url = await config.onUpload(file.file);
          setFiles(prev =>
            prev.map(f =>
              f.id === file.id ? { ...f, status: 'success' as const, url } : f
            )
          );
        } catch (error) {
          setFiles(prev =>
            prev.map(f =>
              f.id === file.id
                ? {
                    ...f,
                    status: 'error' as const,
                    error:
                      error instanceof Error ? error.message : 'Upload failed',
                  }
                : f
            )
          );
        }
      }

      setIsUploading(false);
    }, [files, config]);

    const uploaderProps: WithUploaderProps = {
      files,
      isUploading,
      addFiles,
      removeFile,
      clearFiles,
      uploadFiles,
      errors,
      clearErrors,
      config,
    };

    return <WrappedComponent {...(restProps as P)} {...uploaderProps} />;
  };
};

export default withUploader;
